// Listings V2 performance: Surge BI listing_stats / listing_graph, mapped to the shape the grid,
// drawer and report read.

// --- helpers -------------------------------------------------------------------------------------
// Rules from the BE doc: `null` means "no value from BI" (render as —, never 0); percentages come back
// as raw fractions (-0.4864 => -48.64%), so FE does the x100.
const nullish = (v) => v === null || v === undefined;
const fmtNumber = (v) => (nullish(v) ? null : Number(v).toLocaleString('en-US'));
const deltaMagnitude = (frac) => (nullish(frac) ? null : Math.round(Math.abs(Number(frac)) * 100));
const deltaDir = (frac) => (nullish(frac) ? 0 : Number(frac) > 0 ? 1 : Number(frac) < 0 ? -1 : 0);
const round1 = (x) => Math.round(Number(x) * 10) / 10;

const ordinal = (n) => {
  if (!n) return '';
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

// Rank tone from the peer comparison the listing is actually winning or losing on (design rule), not a
// percentile: green beats the area average with no named peer ahead, amber beats the average but the
// top listing still out-earns it, red is behind the area average.
const rankTone = (mineVsAvg, topVsMine) => {
  if (nullish(mineVsAvg)) return 'none';
  if (mineVsAvg < 1) return 'bad';
  return !nullish(topVsMine) && topVsMine > 1 ? 'warn' : 'good';
};

// Headline line under the rank. The two leads multiples point in opposite directions (see BE doc):
// leads_mine_vs_avg_multiple (>1 = I'm ahead of the pool average) vs leads_top_multiple_vs_mine
// (how many x the best gets over me — bigger is worse).
const buildHeadline = (tone, mineVsAvg, topVsMine) => {
  if (tone === 'good' && !nullish(mineVsAvg) && mineVsAvg >= 1) {
    return { pre: 'You are getting ', strong: `${round1(mineVsAvg)}x`, post: ' more leads than the area average' };
  }
  if (!nullish(topVsMine) && topVsMine > 1) {
    return { pre: 'Top listing is getting ', strong: `${round1(topVsMine)}x`, post: ' more leads than yours' };
  }
  return { pre: '', strong: '', post: '' };
};

// nice round axis ceiling so the trend lines don't hug the top edge.
const niceMax = (m) => {
  if (!m || m <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(m)));
  return Math.ceil(m / pow) * pow;
};

// Maps one /listing_stats entry to the flat performance payload the grid cell + drawer rank read
// (the shape the grid cell and drawer read). A listing with no data returns every metric null.
const mapListingStats = (id, s) => {
  const stat = s || {};
  const noData = nullish(stat.views) && nullish(stat.clicks) && nullish(stat.total_leads);
  const pending = nullish(stat.listing_rank);
  const tone = pending ? 'none' : rankTone(stat.leads_mine_vs_avg_multiple, stat.leads_top_multiple_vs_mine);
  const headline = buildHeadline(tone, stat.leads_mine_vs_avg_multiple, stat.leads_top_multiple_vs_mine);

  // Leads vs the area average, as a multiple (the tooltip shows "VS AREA AVERAGE  1.4x").
  let leadsVs = null;
  if (!nullish(stat.leads_mine_vs_avg_multiple)) {
    leadsVs = { kind: 'average', value: `${round1(stat.leads_mine_vs_avg_multiple)}x`, dir: stat.leads_mine_vs_avg_multiple >= 1 ? 1 : -1 };
  }

  const metric = (value, deltaFrac, vs = null) => ({
    value: fmtNumber(value),
    delta_pct: deltaMagnitude(deltaFrac),
    delta_dir: deltaDir(deltaFrac),
    vs_kind: vs?.kind || null,
    vs_value: vs?.value || null,
    vs_dir: vs?.dir || 0,
  });

  return {
    listing_id: id,
    as_of: new Date().toISOString(),
    runtime: 'Updated daily',
    no_data: noData,
    rank: {
      pending,
      n: stat.listing_rank ?? null,
      ordinal: stat.listing_rank ? ordinal(stat.listing_rank) : '',
      total: stat.pool_size ?? null,
      // Raw ISO: the tooltip turns it into "5 Hours Ago" at render time, so a cached payload does
      // not keep showing the age it had when it was fetched.
      updated_at: stat.rank_updated_at ?? null,
      tone,
      pre: headline.pre,
      strong: headline.strong,
      post: headline.post,
      cta: tone === 'good' ? 'View Performance Details' : 'Improve Listing Rank',
      recalculating: false,
    },
    pool: 'similar listings',
    headline_gap: headline,
    stats: {
      views: metric(stat.views, stat.views_self_delta_pct),
      clicks: metric(stat.clicks, stat.clicks_self_delta_pct),
      leads: metric(stat.total_leads, stat.leads_self_delta_pct, leadsVs),
    },
    // Lead channel breakdown, available to the leads tooltip / drawer if we surface it later.
    channels: {
      whatsapp_clicked: stat.whatsapp_clicked ?? null,
      phone_calls: stat.phone_calls ?? null,
      email_clicked: stat.email_clicked ?? null,
      sms_sent: stat.sms_sent ?? null,
      whatsapp_sent: stat.whatsapp_sent ?? null,
      chats_initiated: stat.chats_initiated ?? null,
      email_sent: stat.email_sent ?? null,
    },
  };
};

// Maps one metric block of /listing_graph to the drawer's chart + stat-card shape. `series` omits
// days with no data, so the arrays are already ordered by date (we carry `dates` for tooltip/x-axis).
const mapGraphMetric = (g) => {
  if (!g) {
    return { total: null, self_delta_pct: null, gap_vs_top_pct: null, vs_average_pct: null, you: [], avg: [], top: [], dates: [], axis: [1] };
  }
  const series = Array.isArray(g.series) ? g.series : [];
  // null is BI's "no value": keep it null so the chart leaves a gap instead of drawing a zero.
  const num = (v) => (nullish(v) ? null : Number(v));
  const you = series.map((p) => num(p.value));
  const avg = series.map((p) => num(p.pool_avg));
  const top = series.map((p) => num(p.pool_top));
  const dates = series.map((p) => p.date);
  const max = Math.max(1, ...[...you, ...avg, ...top].filter((v) => v != null));
  return {
    total: g.total ?? null,
    self_delta_pct: g.self_delta_pct ?? null,
    gap_vs_top_pct: g.gap_vs_top_pct ?? null,
    vs_average_pct: g.vs_average_pct ?? null,
    you,
    avg,
    top,
    dates,
    axis: [niceMax(max)],
  };
};

const mapListingGraph = (graph) => {
  const g = graph || {};
  return {
    Views: mapGraphMetric(g.views),
    Clicks: mapGraphMetric(g.clicks),
    Leads: mapGraphMetric(g.leads),
  };
};

// --- endpoints -----------------------------------------------------------------------------------
const performanceApiEndpoints = {
  // Per-listing grid tiles. Batch — send every listing id on the page. GET /bi/dashboard/listing_stats.
  getListingsPerformance: {
    queryFn: async (params, api, extraOptions, baseQuery) => {
      const { listings = [] } = params || {};
      const ids = listings.map((listing) => listing.id);
      if (!ids.length) return { data: {} };
      const qs = ids.map((id) => `listing_ids[]=${encodeURIComponent(id)}`).join('&');
      const response = await baseQuery(`/api/surge/bi/dashboard/listing_stats?${qs}`);
      if (response?.error) return response;
      const stats = response?.data?.stats || {};
      const data = ids.reduce((acc, id) => {
        acc[id] = mapListingStats(id, stats[id]);
        return acc;
      }, {});
      return { data };
    },
  },

  // Drawer trend chart + range totals for one listing. GET /bi/dashboard/listing_graph. Dates DD-MM-YYYY.
  getListingGraph: {
    queryFn: async (params, api, extraOptions, baseQuery) => {
      const { listingId, startDate, endDate } = params || {};
      if (!listingId || !startDate || !endDate) return { data: mapListingGraph(null) };
      const qs = `listing_id=${encodeURIComponent(listingId)}&start_date=${startDate}&end_date=${endDate}`;
      const response = await baseQuery(`/api/surge/bi/dashboard/listing_graph?${qs}`);
      if (response?.error) return response;
      return { data: mapListingGraph(response?.data?.graph) };
    },
  },

  getListingsQuality: {
    queryFn: async (params, api, extraOptions, baseQuery) => {
      const { listingIds = [] } = params || {};
      const response = await baseQuery(`/api/surge/listings/quality?listing_ids=${listingIds.join(',')}`);
      if (response?.error) return response;
      return { data: response?.data };
    },
  },
  // A short-lived signed URL for an attachment's original file (the uploads bucket, which the page may
  // read, unlike the image CDN). The PDF export uses it to embed the listing photo.
  getAttachmentDownloadUrl: {
    queryFn: async ({ uuid } = {}, api, extraOptions, baseQuery) => {
      if (!uuid) return { data: null };
      const response = await baseQuery(`/api/surge/attachments/presigned_url_without_watermark?uuid=${encodeURIComponent(uuid)}`);
      if (response?.error) return response;
      return { data: response?.data?.media?.url || null };
    },
  },
};

export default performanceApiEndpoints;
